/**
 * The three calls to the save API (server/saveApi.mjs), each reduced to the few outcomes the
 * sync cares about. Nothing here throws and nothing here logs: a server that is missing, asleep
 * or answering HTML is an ordinary outcome, not an error worth the console.
 */

import type { CloudKey, FetchLike, SaveDocument } from './types';
import { isCloudKey } from './types';

/** The health probe runs before the game starts, so it gives up quickly. */
const PROBE_TIMEOUT_MS = 4000;
const REQUEST_TIMEOUT_MS = 15000;
/** The game waits for the pull at start-up, so that one gives up sooner; the merge settles it next time. */
export const BOOT_PULL_TIMEOUT_MS = 8000;
/** Browsers refuse a keepalive request whose body is over 64 KiB; stay clear of the edge. */
export const KEEPALIVE_MAX_BYTES = 60_000;
/** The server's limits (server/saveApi.mjs), so an oversize value is never sent just to be refused. */
export const MAX_VALUE_BYTES = 2 * 1024 * 1024;
export const MAX_BODY_BYTES = 5 * 1024 * 1024;

export type GetResult =
  | { kind: 'ok'; doc: SaveDocument }
  /** Nothing has ever been saved under the code. */
  | { kind: 'none' }
  /** No answer, a server error or a rate limit: worth trying again later. */
  | { kind: 'unreachable' }
  /** The server understood and said no: trying again will not help. */
  | { kind: 'refused'; status: number };

export type PutResult =
  | { kind: 'ok'; revision: number; updatedAt: number }
  | { kind: 'conflict'; doc: SaveDocument }
  | { kind: 'unreachable' }
  | { kind: 'refused'; status: number };

/** Bytes of `text` as UTF-8: what the server's limits and the browser's keepalive limit are measured in. */
export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

function timeout(ms: number): AbortSignal | undefined {
  const able = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
  return able ? AbortSignal.timeout(ms) : undefined;
}

function asDocument(raw: unknown): SaveDocument | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const { revision, updatedAt, keys } = r;
  if (typeof revision !== 'number' || typeof updatedAt !== 'number') return null;
  if (typeof keys !== 'object' || keys === null) return null;
  const clean: Partial<Record<CloudKey, string>> = {};
  for (const [key, value] of Object.entries(keys)) if (isCloudKey(key) && typeof value === 'string') clean[key] = value;
  return { revision, updatedAt, keys: clean };
}

const transient = (status: number): boolean => status >= 500 || status === 429 || status === 408;

export interface Transport {
  probe(): Promise<boolean>;
  get(code: string, timeoutMs?: number): Promise<GetResult>;
  put(code: string, body: string, keepalive: boolean): Promise<PutResult>;
}

export function createTransport(fetchLike: FetchLike, origin: string): Transport {
  const auth = (code: string): Record<string, string> => ({ Authorization: `Bearer ${code}` });

  return {
    async probe() {
      try {
        const signal = timeout(PROBE_TIMEOUT_MS);
        const res = await fetchLike(`${origin}/api/health`, { cache: 'no-store', ...(signal ? { signal } : {}) });
        if (!res.ok) return false;
        // A static host or the dev server answers any path with the game's HTML: that is a no.
        const body = (await res.json()) as unknown;
        return typeof body === 'object' && body !== null && (body as Record<string, unknown>)['saves'] === true;
      } catch {
        return false;
      }
    },

    async get(code, timeoutMs = REQUEST_TIMEOUT_MS) {
      try {
        const signal = timeout(timeoutMs);
        const res = await fetchLike(`${origin}/api/save`, {
          headers: auth(code), cache: 'no-store', ...(signal ? { signal } : {}),
        });
        if (res.status === 404) return { kind: 'none' };
        if (!res.ok) return transient(res.status) ? { kind: 'unreachable' } : { kind: 'refused', status: res.status };
        const doc = asDocument(await res.json());
        return doc === null ? { kind: 'unreachable' } : { kind: 'ok', doc };
      } catch {
        return { kind: 'unreachable' };
      }
    },

    async put(code, body, keepalive) {
      try {
        // The timeout holds for a keepalive request too: if the page outlives it (only hidden, not
        // closed), a request that never ends must not block every later one.
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(`${origin}/api/save`, {
          method: 'PUT',
          headers: { ...auth(code), 'Content-Type': 'application/json' },
          body,
          cache: 'no-store',
          ...(keepalive ? { keepalive: true } : {}),
          ...(signal ? { signal } : {}),
        });
        if (res.status === 409) {
          const doc = asDocument(await res.json());
          return doc === null ? { kind: 'unreachable' } : { kind: 'conflict', doc };
        }
        if (!res.ok) return transient(res.status) ? { kind: 'unreachable' } : { kind: 'refused', status: res.status };
        const raw = (await res.json()) as Record<string, unknown> | null;
        const revision = raw?.['revision'];
        const updatedAt = raw?.['updatedAt'];
        if (typeof revision !== 'number' || typeof updatedAt !== 'number') return { kind: 'unreachable' };
        return { kind: 'ok', revision, updatedAt };
      } catch {
        return { kind: 'unreachable' };
      }
    },
  };
}
